import { CustomTag } from "sipli-fleet-core/components/CustomSharedTag";
import { App, Button, Dropdown } from "antd";
import type { TableProps } from "antd";
import {
  useCallback,
  useMemo,
  useState,
  type ComponentType,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { CrossIcon, DeleteIcon, EditIcon, MenuIcon, SearchIcon } from "src/assets/svgs";

import {
  CustomButton,
  CustomTable,
  CustomTooltip,
  CustomTypography,
} from "sipli-fleet-core/components";
import CustomSharedInput from "sipli-fleet-core/components/CustomSharedInput/CustomSharedInput";
import CustomSharedTitleSection from "sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";

import {
  useMutationDeleteUnitBrand,
  useMutationDeleteUnitModel,
  useMutationUpdateUnitBrand,
  useMutationUpdateUnitModelStatus,
  useQueryGetUnitBrandColumns,
  useQueryGetUnitBrandsList,
  useQueryGetUnitModelColumns,
  useQueryGetUnitModelsList,
} from "src/queries/administrationSetup.queries";

import type {
  SetupListParams,
  UnitBrandRecord,
  UnitModelRecord,
} from "src/services/administrationSetupService";

import { hasAdministrationPermission } from "src/util/administrationPermission";
import UnitModelsAddEdit from "./UnitModelsAddEdit";
import UnitModelsDelete from "./UnitModelsDelete";
import { getColumnOptions, getEntityName, getRecords, getTotalCount, isActive } from "../setupUtils";

type SetupKind = "brand" | "model";
type SetupRecord = UnitBrandRecord | UnitModelRecord;

type SetupSorting = {
  sortBy: string;
  sort: "ASC" | "DESC";
};

const CrossIconComponent = CrossIcon as unknown as ComponentType<{
  className?: string;
}>;

const DeleteIconComponent = DeleteIcon as unknown as ComponentType<{
  className?: string;
}>;

const EditIconComponent = EditIcon as unknown as ComponentType<{
  className?: string;
}>;

const MenuIconComponent = MenuIcon as unknown as ComponentType<{
  className?: string;
}>;

const SearchIconComponent = SearchIcon as unknown as ComponentType<{
  className?: string;
}>;

const { CustomText } = CustomTypography;

const DEFAULT_PAGINATION = {
  page: 0,
  size: 10,
};

const DEFAULT_SORTING: SetupSorting = {
  sortBy: "ID",
  sort: "DESC",
};

const brandDataIndexToColumnId: Record<string, string> = {
  name: "NAME",
  description: "DESCRIPTION",
  status: "STATUS",
};

const modelDataIndexToColumnId: Record<string, string> = {
  name: "NAME",
  code: "CODE",
  commCode: "COMM_CODE",
  unitType: "UNIT_TYPE",
  status: "STATUS",
};

const getDescription = (value: unknown) => {
  const text = typeof value === "string" ? value.trim() : "";

  return text || "--";
};

const DescriptionCell = ({ value }: { value: unknown }) => (
  <CustomTooltip title={getDescription(value)}>
    <span className="block max-w-[320px] truncate">{getDescription(value)}</span>
  </CustomTooltip>
);

const StatusBadge = ({ active }: { active?: boolean | number }) => {
  const { t } = useTranslation();

  return (
    <CustomTag
      className={
        isActive(active)
          ? "bg-green-50 text-green-600"
          : "bg-gray-50 text-gray-400"
      }
    >
      {isActive(active) ? t("active") : t("inactive")}
    </CustomTag>
  );
};

const getRecordSearchText = (record: SetupRecord, kind: SetupKind) => {
  const parts: string[] = [
    record.name ?? "",
    record.description ?? "",
    isActive(record.active) ? "active" : "inactive",
  ];

  if (kind === "model") {
    const model = record as UnitModelRecord;

    parts.push(
      model.code ?? "",
      model.commCode ?? "",
      getEntityName(model.make),
      getEntityName(model.unitType)
    );
  }

  return parts.join(" ").toLowerCase();
};

const getSetupKind = (): SetupKind => "model";

const UnitModelsList = () => {
  const kind = getSetupKind();
  const { t } = useTranslation();
  const { message } = App.useApp();

  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [sorting, setSorting] = useState<SetupSorting>(DEFAULT_SORTING);
  const [search, setSearch] = useState("");
  const [formRecord, setFormRecord] = useState<SetupRecord | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState<SetupRecord | null>(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | number | null>(null);
  const [openActionId, setOpenActionId] = useState<string | number | null>(null);

  const canEdit = hasAdministrationPermission("edit");
  const canDelete = hasAdministrationPermission("delete");

  const isBrand = kind === "brand";

  const queryParams: SetupListParams = useMemo(
    () => ({
      page: pagination.page,
      size: pagination.size,
      sort: sorting.sort,
      sortBy: sorting.sortBy,
    }),
    [pagination.page, pagination.size, sorting.sort, sorting.sortBy]
  );

  const brandQuery = useQueryGetUnitBrandsList(queryParams, {
    enabled: kind === "brand",
  });

  const modelQuery = useQueryGetUnitModelsList(queryParams, {
    enabled: kind === "model",
  });

  const brandColumnsQuery = useQueryGetUnitBrandColumns(kind === "brand");
  const modelColumnsQuery = useQueryGetUnitModelColumns(kind === "model");

  const updateBrandMutation = useMutationUpdateUnitBrand();
  const updateModelStatusMutation = useMutationUpdateUnitModelStatus();

  const deleteBrandMutation = useMutationDeleteUnitBrand();
  const deleteModelMutation = useMutationDeleteUnitModel();

  const activeQuery = isBrand ? brandQuery : modelQuery;
  const response = activeQuery.data?.data?.response;

  const records = getRecords(response, isBrand ? "unitMakes" : "unitModels") as SetupRecord[];

  const filteredRecords = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) return records;

    return records.filter((record) => getRecordSearchText(record, kind).includes(normalizedSearch));
  }, [kind, records, search]);

  const totalCount = search.trim()
    ? filteredRecords.length
    : getTotalCount(response, records.length);

  const columnOptions = useMemo(() => {
    const payload = isBrand ? brandColumnsQuery.data?.data : modelColumnsQuery.data?.data;

    const lookupOptions = getColumnOptions(payload);

    if (lookupOptions.length) return lookupOptions;

    return Object.entries(isBrand ? brandDataIndexToColumnId : modelDataIndexToColumnId)
      .filter(([, value]) => value !== "STATUS")
      .map(([key, value]) => ({
        label: t(key),
        value,
      }));
  }, [brandColumnsQuery.data?.data, isBrand, modelColumnsQuery.data?.data, t]);

  const selectedSortByValue = useMemo(() => {
    const hasSelectedOption = columnOptions.some(
      (option) => String(option.value) === sorting.sortBy
    );

    return hasSelectedOption ? sorting.sortBy : undefined;
  }, [columnOptions, sorting.sortBy]);

  void selectedSortByValue;

  const handleTableChange: TableProps<SetupRecord>["onChange"] = (
    _pagination,
    _filters,
    sorter,
    extra
  ) => {
    if (extra.action === "filter") return;

    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const dataIndex = normalizedSorter?.field || normalizedSorter?.columnKey;

    const columnId = dataIndex
      ? (isBrand ? brandDataIndexToColumnId : modelDataIndexToColumnId)[String(dataIndex)]
      : undefined;

    if (!normalizedSorter?.order || !columnId) {
      setSorting(DEFAULT_SORTING);
    } else {
      setSorting({
        sortBy: columnId,
        sort: normalizedSorter.order === "ascend" ? "ASC" : "DESC",
      });
    }

    setPagination((previous) => ({ ...previous, page: 0 }));
  };

  const openCreateForm = useCallback(() => {
    setFormRecord(null);
    setIsFormOpen(true);
  }, []);

  const openEditForm = useCallback((record: SetupRecord) => {
    setFormRecord(record);
    setIsFormOpen(true);
  }, []);

  const handleStatusChange = useCallback(
    async (record: SetupRecord) => {
      setStatusUpdatingId(record.id);

      try {
        if (isBrand) {
          await updateBrandMutation.mutateAsync({
            id: record.id,
            payload: {
              active: !isActive(record.active),
            },
          });
        } else {
          await updateModelStatusMutation.mutateAsync({
            id: record.id,
            active: !isActive(record.active),
          });
        }

        setOpenActionId(null);
        message.success(t("status_updated"));
      } catch {
        message.error(t("setup_status_update_failed"));
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [isBrand, message, t, updateBrandMutation, updateModelStatusMutation]
  );

  const handleDeleteConfirm = async () => {
    if (!deleteRecord) return;

    try {
      if (isBrand) {
        await deleteBrandMutation.mutateAsync(deleteRecord.id);
      } else {
        await deleteModelMutation.mutateAsync(deleteRecord.id);
      }

      message.success(t(isBrand ? "unit_make_deleted" : "unit_model_deleted"));
      setDeleteRecord(null);
    } catch {
      message.error(t("cannot_delete_item"));
    }
  };

  const renderActions = useCallback(
    (record: SetupRecord): ReactNode => {
      const isCurrentStatusLoading = statusUpdatingId === record.id;

      const disabledActionClass =
        "!cursor-not-allowed !opacity-90 hover:!bg-transparent active:!bg-transparent focus:!bg-transparent !bg-transparent";

      const handleStatusClick = async (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();

        await handleStatusChange(record);
      };

      return (
        <div className="flex items-center justify-center gap-2 users-actions">
          <CustomTooltip title={t("edit")}>
            <CustomButton
              type="icon"
              buttonSize="small"
              aria-label={t("edit_user")}
              disabled={!canEdit}
              className={!canEdit ? disabledActionClass : "focus:!bg-transparent"}
              icon={
                <EditIconComponent
                  className={`w-[16px] h-[16px] ${!canEdit ? "opacity-30 grayscale" : ""}`}
                />
              }
              onClick={(event) => {
                event?.stopPropagation?.();
                openEditForm(record);
              }}
            />
          </CustomTooltip>

          <CustomTooltip title={t("delete")}>
            <CustomButton
              type="icon"
              buttonSize="small"
              aria-label={t("delete_user")}
              disabled={!canDelete}
              className={!canDelete ? disabledActionClass : "focus:!bg-transparent"}
              icon={
                <DeleteIconComponent
                  className={`w-[15px] h-[16px] ${!canDelete ? "opacity-30 grayscale" : ""}`}
                />
              }
              onClick={(event) => {
                event?.stopPropagation?.();
                setDeleteRecord(record);
              }}
            />
          </CustomTooltip>

          <Dropdown
            disabled={!canEdit}
            trigger={["click"]}
            open={openActionId === record.id}
            onOpenChange={(open) => setOpenActionId(open ? record.id : null)}
            dropdownRender={() => (
              <div
                className="min-w-[180px] overflow-hidden rounded-sm bg-white shadow-[0_6px_16px_rgba(0,0,0,0.16)]"
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <Button
                  type="text"
                  loading={isCurrentStatusLoading}
                  className="!flex !h-[36px] !w-full !cursor-pointer !items-center !justify-start !rounded-none !border-0 !bg-white !px-4 !text-left !text-[14px] !text-black hover:!bg-primary6 focus:!bg-white active:!bg-white focus:!outline-none active:!outline-none"
                  onClick={handleStatusClick}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  {isActive(record.active) ? t("inactive") : t("active")}
                </Button>
              </div>
            )}
            placement="bottomRight"
            overlayClassName="user-action-dropdown"
          >
            <CustomTooltip title={t("more_action")}>
              <CustomButton
                type="icon"
                buttonSize="small"
                aria-label={t("more_actions")}
                disabled={!canEdit}
                icon={<MenuIconComponent className="w-[3px] h-[16px]" />}
                className="ml-2 !bg-transparent active:!bg-transparent focus:!bg-transparent"
                onClick={(event) => {
                  event?.stopPropagation?.();
                }}
                onMouseDown={(event) => {
                  event?.preventDefault?.();
                  event?.stopPropagation?.();
                }}
              />
            </CustomTooltip>
          </Dropdown>
        </div>
      );
    },
    [canDelete, canEdit, handleStatusChange, openActionId, openEditForm, statusUpdatingId, t]
  );

  const columns: TableProps<SetupRecord>["columns"] = useMemo(() => {
    const baseColumns: TableProps<SetupRecord>["columns"] = [
      {
        title: t(isBrand ? "name" : "unit_name"),
        dataIndex: "name",
        key: "name",
        sorter: true,
        render: (value: string) => (
          <CustomText className="!text-sm !font-semibold !text-black">{value || "--"}</CustomText>
        ),
      },
    ];

    if (!isBrand) {
      baseColumns.push(
        {
          title: t("unit_make"),
          dataIndex: "make",
          key: "make",
          render: (value) => getEntityName(value) || "--",
        },
        {
          title: t("code"),
          dataIndex: "code",
          key: "code",
          sorter: true,
          render: (value: string) => value || "--",
        },
        {
          title: t("commercial_code"),
          dataIndex: "commCode",
          key: "commCode",
          sorter: true,
          render: (value: string) => value || "--",
        },
        {
          title: t("unit_type"),
          dataIndex: "unitType",
          key: "unitType",
          sorter: true,
          render: (value) => getEntityName(value) || "--",
        }
      );
    }

    baseColumns.push(
      {
        title: t("description"),
        dataIndex: "description",
        key: "description",
        sorter: isBrand,
        render: (value) => <DescriptionCell value={value} />,
      },
      {
        title: t("status"),
        dataIndex: "status",
        key: "status",
        sorter: true,
        render: (_value, record) => <StatusBadge active={record.active} />,
      }
    );

    // if (canEdit || canDelete) {
    baseColumns.push({
      title: t("actions"),
      key: "actions",
      align: "center",
      width: 120,
      render: (_value, record) => renderActions(record),
    });
    // }

    return baseColumns;
  }, [isBrand, renderActions, t]);

  return (
    <section className="pb-8 pl-2 bg-gray1">
      <CustomSharedTitleSection
        titleKey={isBrand ? "unitBrand" : "unitModels"}
        buttonKey="create"
        onClick={openCreateForm}
        showBtn={canEdit}
        buttonProps={{
          className: " !text-gray2 !w-[94px] !h-[40px]",
        }}
        titleClassName="!text-[25.23px] !font-[700]"
      />

      <div className="flex flex-col overflow-hidden bg-white rounded-[16px]">
        <div className="flex flex-wrap items-center gap-3 px-[18px] -mt-2  justify-between">
          <CustomSharedInput
            allowClear={{
              clearIcon: (
                <CrossIconComponent className="mr-0.5 users-search-clear-icon w-[8.5px] h-[8.5px]" />
              ),
            }}
            prefix={<SearchIconComponent className="users-search-icon w-[14px] h-[14px]" />}
            placeholder={t("search")}
            value={search}
            className="global-filter-search-class !rounded-[4px]"
            onChange={(event) => {
              setSearch(event.target.value);
              setPagination((previous) => ({ ...previous, page: 0 }));
            }}
          />

          {/* <div className="flex items-center gap-2 mt-6">
            <CustomSelect
              className="min-w-[170px]"
              placeholder={t("sort_by_column")}
              value={selectedSortByValue}
              options={columnOptions}
              allowClear
              onChange={(value) =>
                setSorting((previous) => ({
                  ...previous,
                  sortBy: value ? String(value) : DEFAULT_SORTING.sortBy,
                }))
              }
              showSearch
              optionFilterProp="label"
            />

            <CustomButton
              type="secondary"
              icon={
                sorting.sort === "ASC" ? (
                  <ArrowUp2 size={16} color="#4B5563" />
                ) : (
                  <ArrowDown2 size={16} color="#4B5563" />
                )
              }
              label={t(sorting.sort === "ASC" ? "ascending" : "descending")}
              onClick={() =>
                setSorting((previous) => ({
                  ...previous,
                  sort: previous.sort === "ASC" ? "DESC" : "ASC",
                }))
              }
            />
          </div> */}
        </div>

        <CustomTable<SetupRecord>
          data={filteredRecords}
          columns={columns}
          rowKey={(record) => String(record.id)}
          isLoading={activeQuery.isLoading}
          pageSize={pagination.size}
          currentPage={pagination.page}
          totalCount={totalCount}
          handlePagination={(page) => setPagination((previous) => ({ ...previous, page }))}
          handlePageSizeChange={(size) =>
            setPagination({
              page: 0,
              size: Number(size),
            })
          }
          showSearchInput={false}
          showCustomizeColumns={false}
          showDownload={false}
          onChange={handleTableChange}
          showTopHorizontalScrollbar
          locale={{ emptyText: t("no_data") }}
        />
      </div>

      <UnitModelsAddEdit
        open={isFormOpen}
        record={formRecord}
        onClose={() => setIsFormOpen(false)}
      />

      <UnitModelsDelete
        open={Boolean(deleteRecord)}
        isLoading={isBrand ? deleteBrandMutation.isPending : deleteModelMutation.isPending}
        onCancel={() => setDeleteRecord(null)}
        onConfirm={handleDeleteConfirm}
      />
    </section>
  );
};

export default UnitModelsList;
